import type { CloudConnectionType, ControlType, GitRefType, ResourceSourceType, TaskListTab, TaskState } from "@octopusdeploy/octopus-server-client";
import { OctopusError } from "@octopusdeploy/octopus-server-client";
import type { AnalyticSession } from "@octopusdeploy/portal-analytics";
import { toErrorCode, useAnalyticLinkLocation, useAnalyticSession } from "@octopusdeploy/portal-analytics";
import type { PageInstanceSummary } from "@octopusdeploy/portal-routes";
import { getPageFields, useDispatchLinkClicked, usePageInstanceSummary } from "@octopusdeploy/portal-routes";
import React, { useCallback, useEffect } from "react";
import type { AuditStreamType } from "~/areas/configuration/components/AuditLayout/AuditStream/AuditStreamType";
import type { CommitType } from "~/areas/platformHub/processTemplates/ProcessTemplateWizardDialog";
import type { ReleaseType } from "~/areas/platformHub/processTemplates/PublishTemplateDialog";
import type { ShareTemplateSpacesType } from "~/areas/platformHub/processTemplates/ShareTemplateDialog";
import type { UpdateBehaviour } from "~/areas/projects/components/Templates/TemplateVersioningSection";
import type { Errors } from "~/components/DataBaseComponent/Errors";
import { isErrors } from "~/components/DataBaseComponent/Errors";
import { formatUrl } from "~/components/Navigation/ExternalLink/ExternalLink";
import { toBoolString } from "~/utils/fieldConverters";
import type { ViewTaskStateTabFields, SampleProjectFields, TenantsWizardFields, ActionedFields, ActionFields, CloudConnectionsFields, DataTableExpansionFields, DeprecationNotificationFields, FilterFields, InsightsFields, LogoFields, OnboardingFields, OtherActionFields as SimpleActionFields, PagingFields, StepEditorFields, StepFields, ViewFields, ViewHelpFields, ConnectTenantsFields, SampleProjectTourFields, GuidedSetupFields, KubernetesAgentSetupFields, LicenseUsageFields, ViewKubernetesAppliedManifestFields, ViewKubernetesObjectDrawerFields, LiveStatusPageFields, LiveStatusSwitchFields, ViewKubernetesAppliedManifestsComparedFields, ViewKubernetesObjectDrawerManifestFields, KubernetesMonitorInstallationStatusFields, ProcessTemplateProducerActionFields, ProcessTemplateConsumerActionFields, LiveInformationSource, } from "./AnalyticSession";
import { LiveInformationSourceTypes } from "./AnalyticSession";
export interface ViewEvent {
    resource: string;
}
export interface ViewProps extends ViewEvent {
    // Override the name by specifying manually
    name?: string;
}
export interface ProjectViewProps extends ViewProps {
    projectId: string;
}
export type ResourceEvent = ViewEvent;
export enum Action {
    Add = "Add",
    Apply = "Apply",
    Autofill = "Autofill",
    Cancel = "Cancel",
    Clear = "Clear",
    Commit = "Commit",
    Configure = "Configure",
    Deploy = "Deploy",
    Dismiss = "Dismiss",
    Download = "Download",
    Edit = "Edit",
    Save = "Save",
    Select = "Select",
    ServerResponse = "Server Response",
    Test = "Test",
    Update = "Update",
    View = "View",
    Toggle = "Toggle",
    Pause = "Pause",
    Delete = "Delete",
    Resume = "Resume",
    RequestTrial = "Request Trial",
    Submit = "Submit",
    OptIn = "Opt In",
    OptOut = "Opt Out",
    Share = "Share"
}
export interface ActionData {
    target?: string;
    stepTemplate?: string[];
    source?: string;
}
export interface ActionEvent extends ResourceEvent {
    action: Action;
    actionMeta?: string;
    data?: ActionData;
    isCaCenabled?: "True" | "False" | undefined; // The type is based on usage, but it should be a boolean
    isDefaultBranch?: boolean;
    commitMessage?: boolean | undefined;
    status?: ActionStatus;
    gitRefType?: GitRefType;
    isProtectedBranch?: boolean | undefined;
    commitBranch?: "Same branch" | "New branch" | undefined;
    plan?: Plan;
    siemProvider?: AuditStreamType;
    location?: "Drawer" | "Page";
    entityType?: "New" | "Existing";
    correlationId?: string;
    processTemplateIds?: string[];
}
export enum ActionStatus {
    Success = "Success",
    SuccessWithWarning = "Success with Warning",
    Failed = "Failed"
}
export enum Plan {
    Enterprise = "Enterprise",
    NonEnterprise = "Non-Enterprise"
}
interface TrackedActionEvent extends ActionEvent {
    error: string[] | undefined;
    statusCode: number | undefined;
    status: ActionStatus;
    duration: number;
}
export interface LinkEvent {
    linkLabel: string;
    documentUrl: string | undefined;
    linkLocation?: string;
}
export interface StepEvent extends ResourceEvent {
    action: Action;
    stepCategory: string;
    stepTemplate: string | undefined;
}
export interface StepEditorEvent extends ResourceEvent {
    action: Action;
    stepTemplate?: string | undefined;
    value?: string;
}
export enum OnboardingType {
    step = "Onboarding Step"
}
export interface OnboardingEvent {
    linkLabel: string;
    onboardingType: OnboardingType;
}
export interface SampleProjectEvent {
    sampleProjectReference: string;
    source?: "Help Sidebar" | "Welcome Dialog";
}
export interface SampleProjectTourEvent extends ResourceEvent {
    action: Action;
    tourStepIndex: number;
    tourStepTitle: string;
    tourStepTarget: string;
    tourState: string;
}
export interface InsightsEvent {
    action: Action;
    inputField?: "Environment" | "Channel" | "Tenant" | "Cadence" | "Split";
    cadence?: string;
    split?: string;
}
export enum LogoType {
    icon = "Icon",
    customImage = "Custom Image"
}
export interface LogoEvent extends ActionEvent, ResourceEvent {
    logoType: LogoType;
}
export interface CloudConnectionsEvent extends ActionEvent {
    cloudProvider: CloudConnectionType;
}
export interface PagingEvent extends ActionEvent {
    itemsPerPage: number;
}
export interface FilterEvent extends ActionEvent {
    numFilterRules: number;
    numUniqueOperators: number;
}
export interface DataTableExpansionEvent extends ActionEvent {
    category: string;
}
export interface ConnectTenantsEvent extends ActionEvent {
    numTenants: number;
}
export interface ViewHelpEvent {
    context: string;
}
export enum NotificationState {
    Raised = "Raised",
    Dismissed = "Dismissed"
}
export interface DeprecationNotificationEvent {
    state: NotificationState;
    deprecation: string;
}
export interface TenantWizardEvent extends ResourceEvent {
    action: Action;
    currentStepName: string;
}
export interface ViewTaskStateTabEvent extends ActionEvent {
    selectedTab: TaskListTab | undefined;
}
export interface GuidedSetupEvent {
    projectId?: string;
    correlationId?: string;
    errors?: string;
    projectType?: "Kubernetes" | "Azure" | "AWS" | "Linux" | "Windows" | "Other";
    projectSubType?: string;
    screen?: "Project" | "Environments" | "Kubernetes Cluster";
    environments?: string;
    versionControl?: boolean;
    sourcePage?: "Project Setup" | "Infrastructure" | string;
    targetType?: "Kubernetes Agent" | "Kubernetes API";
    agentName?: string;
    storageClass?: string;
}
export interface KubernetesAgentSetupEvent {
    errors?: string;
    agentName?: string;
    storageClass?: string;
}
export interface ViewLiveStatusPageEvent {
    projectId: string;
    releaseId: string;
    environmentId: string;
    deploymentId: string;
    deploymentStatus: TaskState;
    liveApplicationHealth: string;
    liveInformationSources: LiveInformationSource[];
}
export interface LicenseUsageEvent {
    resource: string;
    urgency: "info" | "attention" | "danger";
}
export interface ViewKubernetesAppliedManifestEvent {
    deploymentId: string;
    releaseId: string;
    environmentId: string;
    projectId: string;
    deploymentStatus: string;
    view: string;
    executionType: "runbook" | "deployment";
}
export interface ViewKubernetesObjectDrawerEvent {
    releaseId: string;
    environmentId: string;
    deploymentId: string;
    projectId: string;
    objectId: string;
    objectType: string;
    liveApplicationHealth: string;
    deploymentStatus: string;
    objectHealth: string;
    objectSyncStatus: string | null;
    tab: string;
    executionType: "runbook" | "deployment";
}
export interface ViewKubernetesLiveManifestEvent {
    releaseId: string;
    environmentId: string;
    deploymentId: string;
    projectId: string;
    objectId: string;
    objectType: string;
    liveApplicationHealth: string;
    deploymentStatus: string;
    objectHealth: string;
    objectSyncStatus: string | null;
    type: "live" | "applied" | "diff";
    diffViewType: "unified" | "split";
    executionType: "deployment";
}
export interface ViewKubernetesAppliedManifestsComparedEvent {
    deploymentId: string;
    releaseId: string;
    environmentId: string;
    projectId: string;
    deploymentStatus: string;
    executionType: "runbook" | "deployment";
    fromDeploymentId: string;
    toDeploymentId: string;
    diffViewType: "unified" | "split";
}
export interface LiveStatusSwitchEvent {
    projectId: string | null;
    liveStatusSourceTypes: ResourceSourceType[];
    switchEnabled: boolean;
}
export interface KubernetesMonitorInstallationStatusEvent {
    monitorEnabled: boolean;
    monitorStatus?: string;
    agentName: string;
    agentHealth: string;
    storageClassName: string;
    machineType: string;
}
export interface ArgoCDHelmCommandGeneratedEvent {
    gatewayName: string;
    spaceId: string;
}
export interface ProcessTemplateCreatedEvent {
    processTemplateId: string;
}
export interface ProcessTemplateUnsharedEvent {
    type: "Process Template Unshared";
    action: Action.Share;
    sharedTo: ShareTemplateSpacesType.NotShared;
}
export interface ProcessTemplateCommittedEvent {
    type: "Process Template Committed";
    action: Action.Commit;
    stepTemplate: string[];
    parameters: string[];
    commitMessage: boolean;
    commitType: CommitType;
}
export interface ProcessTemplatePublishedEvent {
    type: "Process Template Published";
    action: Action.Add;
    releaseType: ReleaseType;
    isPreRelease: boolean;
}
export interface ProcessTemplateSharedEvent {
    type: "Process Template Shared";
    action: Action.Share;
    sharedTo: ShareTemplateSpacesType;
    spaceCount: number | undefined;
}
export interface ProcessTemplateParameterCreatedEvent {
    type: "Process Template Parameter Created";
    action: Action.Add;
    controlType: ControlType | undefined;
}
export type ProcessTemplateProducerEventNames = "Process Template Unshared" | "Process Template Published" | "Process Template Shared" | "Process Template Parameter Created";
export type ProcessTemplateProducerEvent = ProcessTemplateUnsharedEvent | ProcessTemplatePublishedEvent | ProcessTemplateSharedEvent | ProcessTemplateParameterCreatedEvent;
export type ProcessTemplateCommittedEventName = "Process Template Committed";
export type TrackedProcessTemplateCommittedEvent = ProcessTemplateCommittedEvent & {
    error: string[] | undefined;
    statusCode: number | undefined;
    status: ActionStatus;
};
export enum ProcessTemplateConsumerAction {
    SelectVersionPreference = "Select Version Preference"
}
export type ProcessTemplateConsumerEventNames = "Add Process Template To Process" | "Process Template Version Preferences Set";
export interface AddProcessTemplateToProcessEvent {
    type: "Add Process Template To Process";
    action: Action.Add;
    processTemplateId: string;
}
export interface ProcessTemplateVersionPreferencesSetEvent {
    type: "Process Template Version Preferences Set";
    action: ProcessTemplateConsumerAction.SelectVersionPreference;
    processTemplateId: string;
    versioningPreference: UpdateBehaviour;
    isPreRelease: boolean;
}
export enum TimelineOptionAction {
    Redeploy = "Redeploy",
    Deploy = "Deploy",
    Delete = "Delete",
    TaskLog = "TaskLog"
}
export interface TimelineOptionSelectedEvent {
    deploymentId: string;
    releaseId: string;
    environmentId: string;
    projectId: string;
    selectedOption: TimelineOptionAction;
    selectedOptionReleaseId: string;
}
export type ProcessTemplateConsumerEvent = AddProcessTemplateToProcessEvent | ProcessTemplateVersionPreferencesSetEvent;
export type AnalyticsDispatchEvent<TEvent> = (name: string, event: TEvent) => void;
export type AnalyticErrorCallback = (error: string | OctopusError | Errors) => void;
export type AnalyticTrackedActionDispatcher = <T>(name: string, event: ActionEvent, inner: (cb: AnalyticErrorCallback) => Promise<T>) => Promise<T>;
export type AnalyticActionDispatcher = AnalyticsDispatchEvent<ActionEvent>;
export type AnalyticLinkDispatcher = AnalyticsDispatchEvent<LinkEvent>;
export type AnalyticStepDispatcher = AnalyticsDispatchEvent<StepEvent>;
export type AnalyticStepEditorDispatcher = AnalyticsDispatchEvent<StepEditorEvent>;
export type AnalyticViewHelpDispatcher = AnalyticsDispatchEvent<ViewHelpEvent>;
export type AnalyticOnboardingDispatcher = AnalyticsDispatchEvent<OnboardingEvent>;
export type AnalyticSampleProjectDispatched = AnalyticsDispatchEvent<SampleProjectEvent>;
export type AnalyticViewDispatcher = AnalyticsDispatchEvent<ViewEvent>;
export type AnalyticSimpleDispatcher = (name: string) => void;
export type AnalyticLogoDispatcher = AnalyticsDispatchEvent<LogoEvent>;
export type AnalyticPagingDispatcher = <T>(name: string, event: PagingEvent, inner: () => Promise<T>) => Promise<T>;
export type AnalyticFilterDispatcher = <T>(name: string, event: FilterEvent, inner: () => Promise<T>) => Promise<T>;
export type AnalyticDataTableExpansionDispatcher = AnalyticsDispatchEvent<DataTableExpansionEvent>;
export type AnalyticConnectTenantsDispatcher = AnalyticsDispatchEvent<ConnectTenantsEvent>;
export type AnalyticInsightsDispatcher = AnalyticsDispatchEvent<InsightsEvent>;
export type AnalyticCloudConnectionsDispatcher = AnalyticsDispatchEvent<CloudConnectionsEvent>;
export type AnalyticDeprecationNotificationDispatcher = AnalyticsDispatchEvent<DeprecationNotificationEvent>;
export type AnalyticTenantWizardDispatcher = AnalyticsDispatchEvent<TenantWizardEvent>;
export type AnalyticViewTaskStateTabDispatcher = AnalyticsDispatchEvent<ViewTaskStateTabEvent>;
export type AnalyticGuidedSetupDispatched = AnalyticsDispatchEvent<GuidedSetupEvent>;
export type AnalyticKubernetesAgentSetupDispatcher = AnalyticsDispatchEvent<KubernetesAgentSetupEvent>;
export type LiveStatusPageDispatcher = AnalyticsDispatchEvent<ViewLiveStatusPageEvent>;
export type AnalyticLicenseUsageDispatcher = AnalyticsDispatchEvent<LicenseUsageEvent>;
export type ViewKubernetesAppliedManifestDispatcher = AnalyticsDispatchEvent<ViewKubernetesAppliedManifestEvent>;
export type ViewKubernetesObjectDrawerDispatcher = AnalyticsDispatchEvent<ViewKubernetesObjectDrawerEvent>;
export type ArgoCDHelmCommandGeneratedDispatcher = AnalyticsDispatchEvent<ArgoCDHelmCommandGeneratedEvent>;
export type ViewKubernetesAppliedManifestsComparedDispatcher = AnalyticsDispatchEvent<ViewKubernetesAppliedManifestsComparedEvent>;
export type LiveStatusSwitchDispatcher = AnalyticsDispatchEvent<LiveStatusSwitchEvent>;
export type ViewKubernetesLiveManifestDispatcher = AnalyticsDispatchEvent<ViewKubernetesLiveManifestEvent>;
export type KubernetesMonitorInstallationStatusDispatcher = AnalyticsDispatchEvent<KubernetesMonitorInstallationStatusEvent>;
export type ProcessTemplateCreatedDispatcher = AnalyticsDispatchEvent<ProcessTemplateCreatedEvent>;
export type ProcessTemplateProducerActionDispatcher = (name: ProcessTemplateProducerEventNames, event: ProcessTemplateProducerEvent) => void;
export type TrackedProcessTemplateProducerActionDispatcher = <T>(name: ProcessTemplateCommittedEventName, event: ProcessTemplateCommittedEvent, inner: (cb: AnalyticErrorCallback) => Promise<T>) => Promise<T>;
export type ProcessTemplateConsumerActionDispatcher = (name: ProcessTemplateConsumerEventNames, event: ProcessTemplateConsumerEvent) => void;
export type AnalyticTimelineOptionSelectedDispatcher = AnalyticsDispatchEvent<TimelineOptionSelectedEvent>;
function getDataFields(data?: ActionData) {
    return {
        ...getPropIfDefined("Target Type", data?.target),
        ...getPropIfDefined("Step Template", data?.stepTemplate),
        ...getPropIfDefined("Source", data?.source),
    };
}
export function getErrorFields(statusCode: number | undefined, error: string[] | undefined) {
    return {
        ...getPropIfDefined("Error Code", toErrorCode(statusCode)),
        ...getPropIfDefined("Error Messages", error),
    };
}
function useAnalyticTrackedActionDispatchInternal(projectId?: string | undefined): AnalyticTrackedActionDispatcher {
    const session = useAnalyticSession();
    const page = usePageInstanceSummary();
    function sendTrackedActionEvent(name: string, event: TrackedActionEvent) {
        if (!page) {
            return;
        }
        const fields: ActionFields = {
            "Action Type": event.action,
            "Action Meta": event.actionMeta,
            "Duration (ms)": event.duration.toFixed(2),
            ...getPageFields(page),
            "Resource Type": event.resource,
            Status: event.status,
            ...getDataFields(event.data),
            "Is CaC Enabled": event.isCaCenabled,
            ...getPropIfDefined("Process Template Ids", event.processTemplateIds ? event.processTemplateIds.map((pt) => session.anonymize(pt)) : undefined),
            "Project Id": projectId === undefined ? undefined : session.anonymize(projectId),
            ...getPropIfDefined("Error Code", toErrorCode(event.statusCode)),
            ...getPropIfDefined("Error Messages", event.error),
        };
        if (typeof event.commitMessage === "boolean") {
            fields["Commit Message"] = toBoolString(event.commitMessage);
        }
        if (typeof event.isDefaultBranch === "boolean") {
            fields["Is Default Branch"] = toBoolString(event.isDefaultBranch);
        }
        if (typeof event.isProtectedBranch === "boolean") {
            fields["Is Protected Branch"] = toBoolString(event.isProtectedBranch);
        }
        if (typeof event.commitBranch === "string") {
            fields["Commit Branch"] = event.commitBranch;
        }
        if (typeof event.siemProvider === "string") {
            fields["SIEM Provider"] = event.siemProvider;
        }
        if (typeof event.location === "string") {
            fields["Location"] = event.location;
        }
        if (typeof event.entityType === "string") {
            fields["Entity Type"] = event.entityType;
        }
        if (typeof event.correlationId === "string") {
            fields["Correlation Id"] = event.correlationId;
        }
        session.track(name, fields);
    }
    async function dispatch<T>(name: string, event: ActionEvent, inner: (cb: AnalyticErrorCallback) => Promise<T>): Promise<T> {
        return await measureAsyncAction(inner, (measurements) => {
            sendTrackedActionEvent(name, { ...event, error: measurements.error, status: measurements.status, statusCode: measurements.statusCode, duration: measurements.duration });
        });
    }
    return dispatch;
}
export interface AsyncMeasurements {
    error: string[] | undefined;
    statusCode: number | undefined;
    status: ActionStatus;
    duration: number;
}
export async function measureAsyncAction<T>(action: (cb: AnalyticErrorCallback) => Promise<T>, onMeasurement: (measurements: AsyncMeasurements) => void): Promise<T> {
    const start = performance.now();
    let errorList: Array<string> = [];
    let statusCode: number | undefined = undefined;
    let status: ActionStatus = ActionStatus.Success;
    const addErrors = (errors?: Array<string>, error?: string) => {
        if (errors && errors.length > 0) {
            errorList = [...errorList, ...errors];
        }
        else if (error) {
            errorList = [...errorList, error];
        }
    };
    const onError = (error: string | OctopusError | Errors) => {
        status = ActionStatus.Failed;
        if (error instanceof OctopusError) {
            statusCode = statusCode ?? error.StatusCode;
            addErrors(error.Errors, error.ErrorMessage);
        }
        else if (isErrors(error)) {
            statusCode = statusCode ?? error.statusCode;
            addErrors(error.errors, error.message);
        }
        else {
            addErrors(undefined, error);
        }
    };
    try {
        return await action(onError);
    }
    catch (e) {
        if (e instanceof OctopusError) {
            onError(e);
        }
        else if (e instanceof Error) {
            onError(e.message);
        }
        throw e;
    }
    finally {
        const error = errorList && errorList.length > 0 ? errorList : undefined;
        onMeasurement({
            error,
            status,
            statusCode,
            duration: performance.now() - start,
        });
    }
}
export function useAnalyticTrackedActionDispatch(): AnalyticTrackedActionDispatcher {
    return useAnalyticTrackedActionDispatchInternal();
}
export function useProjectScopedAnalyticTrackedActionDispatch(projectId: string | undefined): AnalyticTrackedActionDispatcher {
    return useAnalyticTrackedActionDispatchInternal(projectId);
}
function useAnalyticActionDispatchInternal(projectId?: string | undefined): AnalyticActionDispatcher {
    const session = useAnalyticSession();
    const page = usePageInstanceSummary();
    return (name: string, event: ActionEvent) => {
        if (!page) {
            return;
        }
        const fields: ActionedFields = {
            "Action Type": event.action,
            ...getPageFields(page),
            "Resource Type": event.resource,
            ...getDataFields(event.data),
            Status: event.status,
            "Project Id": projectId === undefined ? undefined : session.anonymize(projectId),
        };
        if (typeof event.isCaCenabled === "string") {
            fields["Is CaC Enabled"] = event.isCaCenabled;
        }
        if (typeof event.commitMessage === "boolean") {
            fields["Commit Message"] = toBoolString(event.commitMessage);
        }
        if (typeof event.isDefaultBranch === "boolean") {
            fields["Is Default Branch"] = toBoolString(event.isDefaultBranch);
        }
        if (typeof event.gitRefType === "string") {
            fields["Git Ref Type"] = event.gitRefType;
        }
        if (typeof event.plan === "string") {
            fields["Plan"] = event.plan;
        }
        session.track(name, fields);
    };
}
export function useAnalyticActionDispatch(): AnalyticActionDispatcher {
    return useAnalyticActionDispatchInternal();
}
export function useProjectScopedAnalyticActionDispatch(projectId: string | undefined): AnalyticActionDispatcher {
    return useAnalyticActionDispatchInternal(projectId);
}
export function useAnalyticExternalLinkDispatch(): (linkLabel: string, href: string, name: string, linkLocation?: string) => void {
    const dispatchLink = useDispatchLinkClicked();
    return useCallback((linkLabel: string, href: string, name: string, linkLocation?: string) => {
        const ev: LinkEvent = {
            linkLabel,
            documentUrl: formatUrl(href),
            linkLocation,
        };
        dispatchLink(name, ev);
    }, [dispatchLink]);
}
export function nameForDocument() {
    return `Visit Documentation`;
}
export function nameForView(resource: string) {
    return `View ${resource}`;
}
export function useAnalyticStepDispatch(projectId?: string | undefined): AnalyticStepDispatcher {
    const session = useAnalyticSession();
    const page = usePageInstanceSummary();
    return (name: string, event: StepEvent) => {
        if (!page) {
            return;
        }
        const fields: StepFields = {
            "Action Type": event.action,
            ...getPageFields(page),
            "Resource Type": event.resource,
            "Step Category": event.stepCategory,
            ...getPropIfDefined("Step Template", event.stepTemplate),
            "Project Id": projectId === undefined ? undefined : session.anonymize(projectId),
        };
        session.track(name, fields);
    };
}
export function useAnalyticsStepEditorDispatch(stepId: string, projectId?: string | undefined): AnalyticStepEditorDispatcher {
    const session = useAnalyticSession();
    const page = usePageInstanceSummary();
    return (name: string, event: StepEditorEvent) => {
        if (!page) {
            return;
        }
        const fields: StepEditorFields = {
            "Action Type": event.action,
            ...getPageFields(page),
            "Resource Type": event.resource,
            ...getPropIfDefined("Step Template", event.stepTemplate),
            "Project Id": projectId === undefined ? undefined : session.anonymize(projectId),
            "Step Id": session.anonymize(stepId),
            ...getPropIfDefined("Value", event.value),
        };
        session.track(name, fields);
    };
}
export function useAnalyticInsightsDispatch(): AnalyticInsightsDispatcher {
    const session = useAnalyticSession();
    const page = usePageInstanceSummary();
    return (name: string, event: InsightsEvent) => {
        if (!page) {
            return;
        }
        const fields: InsightsFields = {
            "Action Type": event.action,
            ...getPageFields(page),
            ...getPropIfDefined("Insights Input Field", event.inputField),
            ...getPropIfDefined("Cadence", event.cadence),
        };
        session.track(name, fields);
    };
}
export function useAnalyticsViewHelpDispatch(): AnalyticViewHelpDispatcher {
    const session = useAnalyticSession();
    const page = usePageInstanceSummary();
    return (name: string, event: ViewHelpEvent) => {
        if (!page) {
            return;
        }
        const fields: ViewHelpFields = {
            Context: event.context,
            ...getPageFields(page),
        };
        session.track(name, fields);
    };
}
export function useAnalyticOnboardingDispatch(): AnalyticOnboardingDispatcher {
    const session = useAnalyticSession();
    const linkLocation = useAnalyticLinkLocation();
    const page = usePageInstanceSummary();
    return (name: string, event: OnboardingEvent) => {
        if (!page) {
            return;
        }
        const fields: OnboardingFields = {
            "Link Label": event.linkLabel,
            "Link Location": linkLocation ?? "",
            "Onboarding Type": event.onboardingType,
            ...getPageFields(page),
        };
        session.track(name, fields);
    };
}
export function useAnalyticSampleProjectDispatch(): AnalyticSampleProjectDispatched {
    const session = useAnalyticSession();
    return (name: string, event: SampleProjectEvent) => {
        const fields: SampleProjectFields = {
            "Sample Project Reference": event.sampleProjectReference,
            Source: event.source,
        };
        session.track(name, fields);
    };
}
export function useAnalyticSampleProjectTourDispatch(projectId?: string) {
    const session = useAnalyticSession();
    return (name: string, event: SampleProjectTourEvent) => {
        const fields: SampleProjectTourFields = {
            "Action Type": event.action,
            "Resource Type": event.resource,
            "Sample Tour State": event.tourState,
            "Sample Tour Step Index": event.tourStepIndex,
            "Sample Tour Step Title": event.tourStepTitle,
            "Sample Tour Step Target": event.tourStepTarget,
            "Project Id": projectId === undefined ? undefined : session.anonymize(projectId),
        };
        session.track(name, fields);
    };
}
export function useAnalyticLogoDispatch(projectId?: string): AnalyticLogoDispatcher {
    const session = useAnalyticSession();
    const page = usePageInstanceSummary();
    return (name: string, event: LogoEvent) => {
        if (!page) {
            return;
        }
        const fields: LogoFields = {
            "Action Type": Action.Save,
            "Resource Type": event.resource,
            "Logo Type": event.logoType,
            "Project Id": projectId === undefined ? undefined : session.anonymize(projectId),
            ...getPageFields(page),
        };
        session.track(name, fields);
    };
}
export function useAnalyticPagingDispatch(): AnalyticPagingDispatcher {
    const session = useAnalyticSession();
    const page = usePageInstanceSummary();
    async function dispatch<T>(name: string, event: PagingEvent, inner: () => Promise<T>): Promise<T> {
        const start = performance.now();
        try {
            return await inner();
        }
        finally {
            const fields: PagingFields = {
                "Action Type": event.action,
                "Resource Type": event.resource,
                "Items Per Page": event.itemsPerPage,
                "Duration (ms)": (performance.now() - start).toFixed(2),
                ...getPageFields(page),
            };
            session.track(name, fields);
        }
    }
    return dispatch;
}
export function useAnalyticFilterDispatch(): AnalyticFilterDispatcher {
    const session = useAnalyticSession();
    const page = usePageInstanceSummary();
    async function dispatch<T>(name: string, event: FilterEvent, inner: () => Promise<T>): Promise<T> {
        const start = performance.now();
        try {
            return await inner();
        }
        finally {
            const fields: FilterFields = {
                "Action Type": event.action,
                "Resource Type": event.resource,
                "Number of Filter Rules": event.numFilterRules,
                "Number of Unique Operators": event.numUniqueOperators,
                "Duration (ms)": (performance.now() - start).toFixed(2),
                ...getPageFields(page),
            };
            session.track(name, fields);
        }
    }
    return dispatch;
}
export function useAnalyticDataTableExpansionDispatch(): AnalyticDataTableExpansionDispatcher {
    const session = useAnalyticSession();
    const page = usePageInstanceSummary();
    return (name: string, event: DataTableExpansionEvent) => {
        if (!page) {
            return;
        }
        const fields: DataTableExpansionFields = {
            "Action Type": event.action,
            "Resource Type": event.resource,
            Category: event.category,
            ...getPageFields(page),
        };
        session.track(name, fields);
    };
}
export function useAnalyticConnectTenantsDispatch(): AnalyticConnectTenantsDispatcher {
    const session = useAnalyticSession();
    const page = usePageInstanceSummary();
    return (name: string, event: ConnectTenantsEvent) => {
        if (!page) {
            return;
        }
        const fields: ConnectTenantsFields = {
            "Action Type": event.action,
            "Resource Type": event.resource,
            "Number of Tenants": event.numTenants,
            ...getPageFields(page),
        };
        session.track(name, fields);
    };
}
export function useAnalyticCloudConnectionsDispatch(): AnalyticCloudConnectionsDispatcher {
    const session = useAnalyticSession();
    const page = usePageInstanceSummary();
    return (name: string, event: CloudConnectionsEvent) => {
        if (!page) {
            return;
        }
        const fields: CloudConnectionsFields = {
            "Action Type": event.action,
            "Resource Type": event.resource,
            "Cloud Provider": event.cloudProvider,
            ...getPageFields(page),
        };
        session.track(name, fields);
    };
}
export function useAnalyticViewDispatch(projectId?: string | undefined): AnalyticViewDispatcher {
    const session = useAnalyticSession();
    return (name: string, event: ViewEvent) => {
        const fields: ViewFields = {
            "Resource Type": event.resource,
            "Project Id": projectId === undefined ? undefined : session.anonymize(projectId),
        };
        session.track(name, fields);
    };
}
export function useAnalyticSimpleActionDispatch(): AnalyticSimpleDispatcher {
    const session = useAnalyticSession();
    const page = usePageInstanceSummary();
    return (name: string) => {
        if (!page) {
            return;
        }
        const fields: SimpleActionFields = {
            ...getPageFields(page),
        };
        session.track(name, fields);
    };
}
export function useAnalyticDeprecationNotificationDispatch(): AnalyticDeprecationNotificationDispatcher {
    const session = useAnalyticSession();
    return (name: string, event: DeprecationNotificationEvent) => {
        const fields: DeprecationNotificationFields = {
            State: event.state,
            Deprecation: event.deprecation,
        };
        session.track(name, fields);
    };
}
export function useAnalyticConnectTenantWizardDialogDispatch(): AnalyticTenantWizardDispatcher {
    const session = useAnalyticSession();
    const page = usePageInstanceSummary();
    return (name: string, event: TenantWizardEvent) => {
        if (!page) {
            return;
        }
        const fields: TenantsWizardFields = {
            "Action Type": event.action,
            ...getPageFields(page),
            "Resource Type": event.resource,
            Step: event.currentStepName,
        };
        session.track(name, fields);
    };
}
export function useAnalyticViewTaskStateTabDispatch(): AnalyticViewTaskStateTabDispatcher {
    const session = useAnalyticSession();
    const page = usePageInstanceSummary();
    return (name: string, event: ViewTaskStateTabEvent) => {
        if (!page) {
            return;
        }
        const fields: ViewTaskStateTabFields = {
            "Action Type": event.action,
            ...getPageFields(page),
            "Task State": event.selectedTab,
            "Resource Type": event.resource,
        };
        session.track(name, fields);
    };
}
export function useAnalyticGuidedSetupDispatch(projectId?: string | undefined, correlationId?: string | undefined, sourcePage?: string | undefined): AnalyticGuidedSetupDispatched {
    const session = useAnalyticSession();
    return (name: string, event: GuidedSetupEvent) => {
        const projectIdFromEventOrContext = event.projectId ?? projectId;
        const correlationIdFromEventOrContext = event.correlationId ?? correlationId;
        const sourcePageFromEventOrContext = event.sourcePage ?? sourcePage;
        const fields: GuidedSetupFields = {
            "Project Id": projectIdFromEventOrContext === undefined ? undefined : session.anonymize(projectIdFromEventOrContext),
            "Correlation Id": correlationIdFromEventOrContext,
        };
        if (event.screen) {
            fields.Screen = event.screen;
        }
        if (event.projectType) {
            fields["Project Type"] = event.projectType;
        }
        if (event.projectSubType) {
            fields["Project Subtype"] = event.projectSubType;
        }
        if (event.versionControl !== undefined) {
            fields["Use Version Control"] = toBoolString(event.versionControl);
        }
        if (event.environments) {
            fields.Environments = event.environments;
        }
        if (event.targetType) {
            fields["Target Type"] = event.targetType;
        }
        if (sourcePageFromEventOrContext) {
            fields["Source Page"] = sourcePageFromEventOrContext;
        }
        if (event.agentName) {
            fields["Agent Name"] = event.agentName;
        }
        if (event.storageClass) {
            fields["Storage Class"] = event.storageClass;
        }
        session.track(name, fields);
    };
}
export function useAnalyticKubernetesAgentSetupDispatcher(correlationId: string | undefined, sourcePage: "Installation" | "Machine Settings", agentName?: string | undefined): AnalyticKubernetesAgentSetupDispatcher {
    const session = useAnalyticSession();
    return (name: string, event: KubernetesAgentSetupEvent) => {
        const agentNameFromEventOrContext = event.agentName === undefined ? agentName : event.agentName;
        const fields: KubernetesAgentSetupFields = {
            "Correlation Id": correlationId,
            "Source Page": sourcePage,
        };
        if (agentNameFromEventOrContext) {
            fields["Agent Name"] = session.anonymize(agentNameFromEventOrContext);
        }
        if (event.storageClass) {
            fields["Storage Class"] = event.storageClass;
        }
        session.track(name, fields);
    };
}
export function useAnalyticLiveStatusPageDispatch(): LiveStatusPageDispatcher {
    const session = useAnalyticSession();
    return (name: string, event: ViewLiveStatusPageEvent) => {
        const liveInformationSources = [...new Set(event.liveInformationSources)].sort();
        const fields: LiveStatusPageFields = {
            "Project Id": session.anonymize(event.projectId),
            "Release Id": session.anonymize(event.releaseId),
            "Environment Id": session.anonymize(event.environmentId),
            "Deployment Id": session.anonymize(event.deploymentId),
            "Deployment Status": event.deploymentStatus,
            "Live Application Health": event.liveApplicationHealth,
            "Live Information Source": liveInformationSources,
        };
        session.track(name, fields);
    };
}
export function useAnalyticLicenseUsageDispatch(): AnalyticLicenseUsageDispatcher {
    const session = useAnalyticSession();
    return (name: string, event: LicenseUsageEvent) => {
        const fields: LicenseUsageFields = {
            Urgency: event.urgency,
            Resource: event.resource,
        };
        session.track(name, fields);
    };
}
export function useAnalyticViewKubernetesAppliedManifestDispatch(): ViewKubernetesAppliedManifestDispatcher {
    const session = useAnalyticSession();
    return (name: string, event: ViewKubernetesAppliedManifestEvent) => {
        const fields: ViewKubernetesAppliedManifestFields = {
            "Deployment Id": session.anonymize(event.deploymentId),
            "Release Id": session.anonymize(event.releaseId),
            "Environment Id": session.anonymize(event.environmentId),
            "Project Id": session.anonymize(event.projectId),
            "Deployment Status": event.deploymentStatus,
            "Execution Type": event.deploymentId ? "deployment" : "runbook",
            View: event.view,
        };
        session.track(name, fields);
    };
}
export function useAnalyticViewKubernetesObjectDrawerDispatch(): ViewKubernetesObjectDrawerDispatcher {
    const session = useAnalyticSession();
    const page = usePageInstanceSummary();
    return (name: string, event: ViewKubernetesObjectDrawerEvent) => {
        const fields: ViewKubernetesObjectDrawerFields = {
            "Release Id": session.anonymize(event.releaseId),
            "Execution Type": event.deploymentId ? "deployment" : "runbook",
            "Environment Id": session.anonymize(event.environmentId),
            "Deployment Id": session.anonymize(event.deploymentId),
            "Project Id": session.anonymize(event.projectId),
            "Page Id": page.Id,
            "Object Id": session.anonymize(event.objectId),
            "Object Type": event.objectType,
            "Live Application Health": event.liveApplicationHealth,
            "Deployment Status": event.deploymentStatus,
            "Object Health": event.objectHealth,
            "Object Sync Status": event.objectSyncStatus,
            Tab: event.tab,
        };
        session.track(name, fields);
    };
}
export function useAnalyticLiveStatusManifestDispatch(): ViewKubernetesLiveManifestDispatcher {
    const session = useAnalyticSession();
    const page = usePageInstanceSummary();
    return (name: string, event: ViewKubernetesLiveManifestEvent) => {
        const fields: ViewKubernetesObjectDrawerManifestFields = {
            "Release Id": session.anonymize(event.releaseId),
            "Execution Type": event.deploymentId ? "deployment" : "runbook",
            "Environment Id": session.anonymize(event.environmentId),
            "Deployment Id": session.anonymize(event.deploymentId),
            "Project Id": session.anonymize(event.projectId),
            "Page Id": page.Id,
            "Object Id": session.anonymize(event.objectId),
            "Object Type": event.objectType,
            "Live Application Health": event.liveApplicationHealth,
            "Deployment Status": event.deploymentStatus,
            "Object Health": event.objectHealth,
            "Object Sync Status": event.objectSyncStatus,
            Type: event.type,
            "Diff View Type": event.diffViewType,
        };
        if (event.projectId) {
            fields["Project Id"] = session.anonymize(event.projectId);
        }
        session.track(name, fields);
    };
}
export function useAnalyticViewKubernetesAppliedManifestComparedDispatch(): ViewKubernetesAppliedManifestsComparedDispatcher {
    const session = useAnalyticSession();
    return (name: string, event: ViewKubernetesAppliedManifestsComparedEvent) => {
        const fields: ViewKubernetesAppliedManifestsComparedFields = {
            "Deployment Id": session.anonymize(event.deploymentId),
            "Release Id": session.anonymize(event.releaseId),
            "Environment Id": session.anonymize(event.environmentId),
            "Project Id": session.anonymize(event.projectId),
            "Deployment Status": event.deploymentStatus,
            "Execution Type": event.deploymentId ? "deployment" : "runbook",
            "From Deployment Id": session.anonymize(event.fromDeploymentId),
            "To Deployment Id": session.anonymize(event.toDeploymentId),
            "Diff View Type": event.diffViewType,
        };
        session.track(name, fields);
    };
}
export function useAnalyticLiveStatusSwitchDispatch(): LiveStatusSwitchDispatcher {
    const session = useAnalyticSession();
    const page = usePageInstanceSummary();
    return (name: string, event: LiveStatusSwitchEvent) => {
        const liveInformationSources: LiveInformationSource[] = [...new Set(event.liveStatusSourceTypes)]
            .map((x) => {
            if (x === "KubernetesMonitor") {
                return LiveInformationSourceTypes.KubernetesMonitor;
            }
            if (x === "ArgoCDInstance" || x === "ArgoCDApplication") {
                return LiveInformationSourceTypes.ArgoCD;
            }
            return LiveInformationSourceTypes.Unknown;
        })
            .sort();
        const fields: LiveStatusSwitchFields = {
            "Page Id": page.Id,
            "Switch Enabled": event.switchEnabled,
            "Live Information Source": liveInformationSources,
        };
        if (event.projectId) {
            fields["Project Id"] = session.anonymize(event.projectId);
        }
        session.track(name, fields);
    };
}
export function useAnalyticKubernetesMonitorInstallationStatusDispatch(): KubernetesMonitorInstallationStatusDispatcher {
    const session = useAnalyticSession();
    return (name: string, event: KubernetesMonitorInstallationStatusEvent) => {
        const fields: KubernetesMonitorInstallationStatusFields = {
            "Monitor Enabled": event.monitorEnabled,
            "Agent Name": session.anonymize(event.agentName),
            "Agent Health": event.agentHealth,
            "Storage Class Name": event.storageClassName,
            "Machine Type": event.machineType,
        };
        if (event.monitorStatus) {
            fields["Monitor Status"] = event.monitorStatus;
        }
        session.track(name, fields);
    };
}
export function useAnalyticProcessTemplateCreatedDispatch(isDefaultBranch: boolean, isProtectedBranch: boolean): ProcessTemplateCreatedDispatcher {
    const session = useAnalyticSession();
    const page = usePageInstanceSummary();
    return (name: string, event: ProcessTemplateCreatedEvent) => {
        const fields: ProcessTemplateProducerActionFields = {
            "Process Template Id": session.anonymize(event.processTemplateId),
            "Action Type": Action.Add,
            ...getPageFields(page),
            "Resource Type": "Process Template",
            "Is Default Branch": toBoolString(isDefaultBranch),
            "Is Protected Branch": toBoolString(isProtectedBranch),
        };
        session.track(name, fields);
    };
}
export function useAnalyticProcessTemplateProducerActionDispatch(processTemplateSlug: string, isDefaultBranch: boolean, isProtectedBranch: boolean): ProcessTemplateProducerActionDispatcher {
    const session = useAnalyticSession();
    const page = usePageInstanceSummary();
    return (name: ProcessTemplateProducerEventNames, event: ProcessTemplateProducerEvent) => {
        let fields: ProcessTemplateProducerActionFields = getProcessTemplateProducerCommonFields(session, processTemplateSlug, event, page, isDefaultBranch, isProtectedBranch);
        if (event.type === "Process Template Published") {
            fields = {
                ...fields,
                ...getPropIfDefined("Release Type", event.releaseType),
                "Is Pre-Release": event.isPreRelease,
            };
        }
        else if (event.type === "Process Template Shared") {
            fields = {
                ...fields,
                ...getPropIfDefined("Shared To", event.sharedTo),
                "Space Count": event.spaceCount,
            };
        }
        else if (event.type === "Process Template Unshared") {
            fields = {
                ...fields,
                ...getPropIfDefined("Shared To", event.sharedTo),
            };
        }
        else if (event.type === "Process Template Parameter Created") {
            fields = {
                ...fields,
                ...getPropIfDefined("Control Type", event.controlType),
            };
        }
        session.track(name, fields);
    };
}
export function useAnalyticTimelineOptionSelectedDispatch(): AnalyticTimelineOptionSelectedDispatcher {
    const session = useAnalyticSession();
    return (name: string, event: TimelineOptionSelectedEvent) => {
        const fields = {
            "Deployment ID": session.anonymize(event.deploymentId),
            "Release ID": session.anonymize(event.releaseId),
            "Environment Id": session.anonymize(event.environmentId),
            "Project ID": session.anonymize(event.projectId),
            "Selected option": event.selectedOption,
            "Selected option Release Id": session.anonymize(event.selectedOptionReleaseId),
        };
        session.track(name, fields);
    };
}
export function useAnalyticTrackedProcessTemplateProducerActionDispatch(processTemplateSlug: string, isDefaultBranch: boolean, isProtectedBranch: boolean): TrackedProcessTemplateProducerActionDispatcher {
    const session = useAnalyticSession();
    const page = usePageInstanceSummary();
    function sendTrackedActionEvent(name: ProcessTemplateCommittedEventName, event: TrackedProcessTemplateCommittedEvent) {
        if (!page) {
            return;
        }
        const fields: ProcessTemplateProducerActionFields = {
            ...getProcessTemplateProducerCommonFields(session, processTemplateSlug, event, page, isDefaultBranch, isProtectedBranch),
            ...getPropIfDefined("Step Template", event.stepTemplate),
            ...getPropIfDefined("Parameters", event.parameters),
            ...getPropIfDefined("Commit Type", event.commitType),
            ...getPropIfDefined("Error Code", toErrorCode(event.statusCode)),
            ...getPropIfDefined("Error Messages", event.error),
            "Commit Message": toBoolString(event.commitMessage),
        };
        session.track(name, fields);
    }
    async function dispatch<T>(name: ProcessTemplateCommittedEventName, event: ProcessTemplateCommittedEvent, inner: (cb: AnalyticErrorCallback) => Promise<T>): Promise<T> {
        return await measureAsyncAction(inner, (measurements) => {
            sendTrackedActionEvent(name, { ...event, error: measurements.error, status: measurements.status, statusCode: measurements.statusCode });
        });
    }
    return dispatch;
}
function getProcessTemplateProducerCommonFields(session: AnalyticSession, processTemplateSlug: string, event: ProcessTemplateProducerEvent | ProcessTemplateCommittedEvent, page: PageInstanceSummary, isDefaultBranch: boolean, isProtectedBranch: boolean) {
    return {
        "Process Template Id": session.anonymize(processTemplateSlug),
        "Action Type": event.action,
        ...getPageFields(page),
        "Resource Type": "Process Template",
        "Is Default Branch": toBoolString(isDefaultBranch),
        "Is Protected Branch": toBoolString(isProtectedBranch),
    };
}
export function useAnalyticProcessTemplateConsumerActionDispatch(projectId: string, resource: "Deployment Process" | "Runbook"): ProcessTemplateConsumerActionDispatcher {
    const session = useAnalyticSession();
    const page = usePageInstanceSummary();
    return (name: string, event: ProcessTemplateConsumerEvent) => {
        let fields: ProcessTemplateConsumerActionFields = {
            "Process Template Id": session.anonymize(event.processTemplateId),
            "Action Type": event.action,
            ...getPageFields(page),
            "Resource Type": resource,
            "Project Id": session.anonymize(projectId),
        };
        if (event.type === "Process Template Version Preferences Set") {
            fields = {
                ...fields,
                "Versioning Preference": event.versioningPreference,
                "Is Pre-Release": event.isPreRelease,
            };
        }
        session.track(name, fields);
    };
}
// <AnalyticView> element allows for the sending of a once-off view event when the element is rendered on the page
export function AnalyticView(props: ViewProps) {
    const dispatch = useAnalyticViewDispatch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => dispatch(props.name ?? nameForView(props.resource), { resource: props.resource }), []);
    return <></>;
}
export function ProjectAnalyticView(props: ProjectViewProps) {
    const dispatch = useAnalyticViewDispatch(props.projectId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => dispatch(props.name ?? nameForView(props.resource), { resource: props.resource }), []);
    return <></>;
}
function getPropIfDefined(name: string, value?: string | string[]): object {
    return value !== undefined ? { [name]: value } : {};
}
export function useCorrelationId(): string | undefined {
    // generate a random base64 string of 16 characters
    const [correlationId] = React.useState(() => btoa(Math.random().toString(36).substring(2, 18)));
    return correlationId;
}
